import { apiClient } from './client';

// ── Types ─────────────────────────────────────────────────────

export type AuditLogAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'BULK_CREATE'
  | 'BULK_UPDATE'
  | 'BULK_DELETE';

export interface AuditLogListItem {
  id: string;
  modelName: string;
  recordId: string;
  action: AuditLogAction;
  actorId: string | null;
  actorName: string;
  timestamp: string;
}

export interface AuditLogDetail extends AuditLogListItem {
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  snapshot: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

export interface PaginatedAuditLogsResponse {
  data: AuditLogListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogsQueryParams {
  modelName?: string;
  recordId?: string;
  actorId?: string;
  action?: AuditLogAction;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

// ── Helper ────────────────────────────────────────────────────

function buildQueryString(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

// ── API Functions ─────────────────────────────────────────────

/**
 * Fetch paginated audit logs with optional filters.
 * GET /audit-logs
 */
export function fetchAuditLogs(
  params: AuditLogsQueryParams = {},
): Promise<PaginatedAuditLogsResponse> {
  const qs = buildQueryString(params as Record<string, string | number | undefined>);
  return apiClient<PaginatedAuditLogsResponse>(`/audit-logs${qs}`);
}

/**
 * Fetch a single audit log entry by ID.
 * GET /audit-logs/:id
 */
export function fetchAuditLogById(id: string): Promise<AuditLogDetail> {
  return apiClient<AuditLogDetail>(`/audit-logs/${id}`);
}

/**
 * Fetch the complete audit history for a specific record.
 * GET /audit-logs/record/:modelName/:recordId
 */
export function fetchAuditLogsByRecord(
  modelName: string,
  recordId: string,
): Promise<AuditLogListItem[]> {
  return apiClient<AuditLogListItem[]>(`/audit-logs/record/${modelName}/${recordId}`);
}
